import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  classMap,
  OmniImgUploadElement,
  ImageAPI,
} from 'omni-ui';
import { om2alert } from 'omni-campaign-ui';

import { MAX_IMAGE_BYTE_SIZE } from '../../helpers/constants.js';
import './persona-base-card.js';
import '../campaign-wysiwyg.js';

OmniStyleElement.register();
OmniImgUploadElement.register();

/**
 * @summary Class for PersonaSummaryCard
 * @description Card with the overall summary of the persona
 * @extends OmniElement
 */
export class PersonaSummaryCard extends OmniElement {
  /**
   * @property {String} ANsid - session id needed by ImageAPI
   * @property {Object} internalState - Internal state of the class before updating parent state
   * @property {Boolean} isEditView - tracks which view PersonaSummaryCard is on
   * @property {Boolean} readOnly - Boolean determining if content is readOnly or editable
   * @property {Object} summaryData - State passed down by parent component: {
      audienceAge: '',
      audienceDescription: '',
      audienceName: '',
      cardType: 'persona-card',
      imageURL: '',
      personaName: '',
      title: 'Persona Card',
    }
   */
  static get properties() {
    return {
      internalState: { type: Object },
      isEditView: { type: Boolean },
      readOnly: { type: Boolean },
      summaryData: { type: Object },
    };
  }

  /**
   * PersonaSummaryCard module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        /* General Rules */
        :host,
        omni-style.omni,
        .omni .box,
        persona-base-card {
          display: flex;
          flex: 1 1 auto;
          min-height: 35.14rem; /*force browsers to allow card to shrink and not overflow container*/
        }

        omni-img-upload {
          margin-bottom: 36px;
          align-self: center;
        }

        omni-img-upload[disabled] {
          cursor: default;
        }

        omni-img-upload[disabled].has-image {
          opacity: 1;
        }

        omni-img-upload[disabled]::part(upload-icon) {
          display: none;
        }

        div.persona-meta-data {
          min-width: 14.5rem;
          display: flex;
          flex-flow: column nowrap;
          align-items: center;
          border-bottom: 0.07rem solid var(--color-shark);
          margin-bottom: 1.43rem;
        }

        span.persona-name {
          font-size: 2.57rem;
          font-weight: 300;
          line-height: 1;
          letter-spacing: 0.8px;
          color: var(--rgb-core-dark);
          margin-bottom: 0.86rem;
          text-align: center;
        }

        span.audience-name {
          font-weight: 600;
          line-height: normal;
          color: var(--rgb-core-dark);
          margin-bottom: 0.57rem;
        }

        span.audience-age {
          font-size: 0.86rem;
          font-weight: 600;
          line-height: normal;
          letter-spacing: normal;
          color: var(--rgb-core-dark);
          margin-bottom: 1.71rem;
        }

        .opacity {
          opacity: 0.5;
        }

        .content-container {
          display: flex;
          overflow: clip;
          flex: 1 1 auto;
          min-height: 0;
        }

        .content-placeholder {
          justify-content: center;
          align-items: center;
          text-align: center;
        }

        .content {
          justify-content: flex-start;
        }

        .edit-view::part(base-card-container) {
          min-width: 26.5rem;
        }

        div.edit-view omni-img-upload {
          align-self: center;
        }

        div.field > label.label {
          font-size: 0.79rem;
          color: var(--rgb-core-dark);
        }

        .omni div.field.input-container {
          margin-bottom: 2.14rem;
        }

        .omni div.field.text-container {
          display: flex;
          flex-flow: column nowrap;
          flex: 1 1 auto;
          min-height: 0;
          margin-bottom: 1.71rem;
        }

        div.control.text-area {
          height: 14.29rem;
        }
      `,
    ];
  }

  /**
   * Initializes {@link #ANsid} {@link #internalState}, {@link #isEditView}, and {@link #readOnly}
   */
  constructor() {
    super();
    this.ANsid = window?.AN?.sso?.session?.sid;
    this.internalState = {};
    this.isEditView = false;
    this.readOnly = false;
  }

  /**
   * Instantiates {@link #internalState} to {@link #summaryData}
   */
  connectedCallback() {
    super.connectedCallback();
    this.internalState = this.summaryData;
  }

  /**
   * @param {CampaignWYSIWYG#_handleContentChange~event:change} event - object containing event detail which is the value from the WYSIWYG editor content
   */
  _onEditorChange(event) {
    const { value } = event.detail;
    this.internalState = produce(this.internalState, draft => {
      draft.audienceDescription = value;
    });
  }

  /**
   * @summary  Updates {@link #summaryData} to the spread of {@link #internalState} and emits @event persona-change that {@link CampaignAudiencePersona#_updatePersonaData} listens to and updates the modules state with.
   */
  _updatePersonaData = () => {
    this.summaryData = { ...this.internalState };
    this.dispatchNewEvent('persona-change', {
      detail: { cardData: this.summaryData },
    });
    this.isEditView = false;
  };

  /**
   * @param {OmniImgUpload~event:change} e - invoked on omni-img-upload change event. Takes the picture file object from the event's detail and sends it to the ImageAPI to and returns an image URL string to be used
   */
  async _setImage(e) {
    e.stopPropagation();
    const imgInputElement = e.target;
    imgInputElement.reportValidity();
    const { file } = e.detail;
    if (file === null || file === undefined || file === '') {
      this.internalState = produce(this.internalState, draft => {
        draft.imageURL = '';
      });
      return;
    }
    try {
      if (!this.ANsid) throw new Error("Missing ANsid: can't upload images");
      const url = await new ImageAPI(this.ANsid, this.env).createImageURL(file);
      this.internalState = produce(this.internalState, draft => {
        draft.imageURL = url;
      });
    } catch (e2) {
      if (e2.message) {
        om2alert(e2.message);
      } else {
        try {
          const error = await e2.json();
          om2alert(error.message);
        } catch {
          om2alert('Image upload failed');
        }
      }
    }
  }

  /**
   * @returns {TemplateResult} html to render display view
   */
  _renderDisplayView = () => html`
    <div class="persona-meta-data">
      <span
        class=${classMap({
          'persona-name': true,
          opacity: !this.summaryData?.personaName,
        })}
      >
        ${this.summaryData?.personaName
          ? html`${this.summaryData.personaName}`
          : 'Persona'}
      </span>
      <span
        class=${classMap({
          'audience-name': true,
          opacity: !this.summaryData?.audienceName,
        })}
      >
        ${this.summaryData?.audienceName
          ? html`${this.summaryData.audienceName}`
          : ''}
      </span>
      <span
        class=${classMap({
          'audience-age': true,
          opacity: !this.summaryData?.audienceAge,
        })}
      >
        ${this.summaryData?.audienceAge
          ? html`${this.summaryData.audienceAge}`
          : ''}
      </span>
    </div>
    <div
      class=${classMap({
        'content-container': true,
        'content-placeholder': !this.summaryData?.audienceDescription,
        content: this.summaryData?.audienceDescription,
      })}
    >
      ${this.summaryData?.audienceDescription
        ? html`<campaign-wysiwyg
            placeholder=${this.summaryData?.editorPlaceholder}
            .initialContent=${this.summaryData?.audienceDescription}
            ?display-only=${!this.isEditView}
          ></campaign-wysiwyg> `
        : ''}
    </div>
  `;

  /**
   * @returns {TemplateResult} html to render edit view
   */
  _renderEditView = () => html`
    <div class="field input-container">
      <label class="label">Persona Name</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Enter persona name"
          .value=${this.internalState?.personaName}
          @input=${e => {
            const { value } = e.target;
            this.internalState = produce(this.internalState, draft => {
              draft.personaName = value;
            });
          }}
        />
      </div>
    </div>
    <div class="field input-container">
      <label class="label">Audience Name</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Enter audience name"
          .value=${this.internalState?.audienceName}
          @input=${e => {
            const { value } = e.target;
            this.internalState = produce(this.internalState, draft => {
              draft.audienceName = value;
            });
          }}
        />
      </div>
    </div>
    <div class="field input-container">
      <label class="label">Audience Age</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Enter audience age"
          .value=${this.internalState?.audienceAge}
          @input=${e => {
            const { value } = e.target;
            this.internalState = produce(this.internalState, draft => {
              draft.audienceAge = value;
            });
          }}
        />
      </div>
    </div>
    <div class="field text-container">
      <label class="label">Audience description</label>
      <div class="control text-area">
        <campaign-wysiwyg
          @change=${this._onEditorChange}
          placeholder=${this.internalState?.editorPlaceholder}
          .initialContent=${this.internalState?.audienceDescription}
        ></campaign-wysiwyg>
      </div>
    </div>
  `;

  /**
   * @returns {TemplateResult} main HTML to be rendered on the DOM
   */
  render() {
    return html`
      <omni-style>
        <persona-base-card
          class=${classMap({ 'edit-view': this.isEditView })}
          .isEditView=${this.isEditView}
          .readOnly=${this.readOnly}
          @persona-card-close=${() => {
            this.isEditView = false;
          }}
          @persona-card-submit=${this._updatePersonaData}
          @persona-card-edit-click=${() => {
            this.isEditView = true;
          }}
        >
          <omni-img-upload
            height="140"
            width="140"
            maxSize=${MAX_IMAGE_BYTE_SIZE}
            class=${classMap({
              'has-image': this.summaryData.imageURL,
            })}
            ?disabled=${!this.isEditView}
            img=${this.isEditView
              ? this.internalState?.imageURL
              : this.summaryData?.imageURL}
            @change=${this._setImage}
          ></omni-img-upload>
          ${this.isEditView
            ? this._renderEditView()
            : this._renderDisplayView()}
        </persona-base-card>
      </omni-style>
    `;
  }
}

OmniElement.register('persona-summary-card', PersonaSummaryCard);
